import { useCallback, useEffect, useRef, useState } from 'react';
import { loadListener, loadSpeaker, recordUtterance, speakNeural } from './neuralSpeech.ts';

/**
 * Voice I/O for the simulator, with barge-in: when the customer starts talking while Alexa is
 * speaking, Alexa stops at once and the barge-in latency is recorded.
 *
 * - browser: Web Speech recognition (push-to-talk) and the browser's speech synthesis.
 * - neural: on-device Moonshine and Kokoro (see neuralSpeech.ts); works in any modern browser,
 *   and the microphone's audio never leaves the page.
 */
export type VoiceMode = 'browser' | 'neural';

type Recognition = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: any) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: any) => void) | null;
  onspeechstart: (() => void) | null;
};

function makeRecognition(): Recognition | undefined {
  const Ctor = (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition;
  if (!Ctor) return undefined;
  const r = new Ctor() as Recognition;
  r.lang = 'en-US';
  r.interimResults = true;
  r.continuous = false;
  return r;
}

function pickVoice(): SpeechSynthesisVoice | undefined {
  const voices = window.speechSynthesis?.getVoices() ?? [];
  const preferred = ['Samantha', 'Google US English', 'Microsoft Aria', 'Microsoft Jenny', 'Joanna', 'Allison'];
  for (const name of preferred) {
    const v = voices.find((x) => x.name.includes(name));
    if (v) return v;
  }
  return voices.find((v) => v.lang === 'en-US') ?? voices[0];
}

export function useVoice(onFinal: (text: string) => void, mode: VoiceMode = 'browser') {
  const [status, setStatus] = useState('');
  const neuralRec = useRef<{ stop: () => void } | undefined>(undefined);
  const neuralSpeech = useRef<{ stop: () => void } | undefined>(undefined);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [speaking, setSpeaking] = useState(false);
  const [muted, setMuted] = useState(false);
  const [bargeInMs, setBargeInMs] = useState<number[]>([]);
  const recRef = useRef<Recognition | undefined>(undefined);
  const finalRef = useRef(onFinal);
  finalRef.current = onFinal;
  const supported =
    typeof window !== 'undefined' &&
    (mode === 'neural' ? Boolean(navigator.mediaDevices?.getUserMedia) && typeof AudioContext !== 'undefined' : Boolean((window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition));

  // Turning the neural voice on starts both downloads, with progress, so the first reply isn't a wait.
  useEffect(() => {
    if (mode !== 'neural') return;
    let live = true;
    let settled = false;
    const say = (m: string) => live && !settled && setStatus(m);
    void Promise.all([loadSpeaker(say), loadListener(say)])
      .then(() => say('On-device voice ready.'))
      .catch((err: unknown) => say(`The on-device voice couldn't load here (${(err as Error).message.slice(0, 80)}); type instead.`))
      .finally(() => (settled = true));
    return () => {
      live = false;
    };
  }, [mode]);

  useEffect(() => {
    window.speechSynthesis?.getVoices();
    return () => {
      recRef.current?.abort();
      window.speechSynthesis?.cancel();
    };
  }, []);

  /** Stop Alexa mid-sentence. A barge-in (the customer started talking) is timed; a quiet stop is not. */
  const stopSpeaking = useCallback((bargeIn = true) => {
    if (neuralSpeech.current) {
      const t0 = performance.now();
      neuralSpeech.current.stop();
      neuralSpeech.current = undefined;
      if (bargeIn) setBargeInMs((xs) => [...xs.slice(-19), Math.round(performance.now() - t0)]);
    }
    if (window.speechSynthesis?.speaking) {
      const t0 = performance.now();
      window.speechSynthesis.cancel();
      if (bargeIn) setBargeInMs((xs) => [...xs.slice(-19), Math.round(performance.now() - t0)]);
    }
    setSpeaking(false);
  }, []);

  const listen = useCallback(() => {
    if (!supported) return;
    stopSpeaking();
    if (mode === 'neural') {
      setListening(true);
      setInterim('Listening…');
      void (async () => {
        try {
          const hear = await loadListener(setStatus);
          const t0 = performance.now();
          const rec = recordUtterance();
          neuralRec.current = rec;
          const audio = await rec.done;
          const t1 = performance.now();
          setListening(false);
          setInterim('Transcribing on this device…');
          const text = await hear(audio);
          console.debug(`[voice] listened ${Math.round(t1 - t0)} ms for ${(audio.length / 16000).toFixed(1)} s of speech; transcribed in ${Math.round(performance.now() - t1)} ms`);
          setInterim('');
          if (text) finalRef.current(text);
        } catch {
          setListening(false);
          setInterim('');
          setStatus('Microphone unavailable; type instead.');
        }
      })();
      return;
    }
    recRef.current?.abort();
    const r = makeRecognition()!;
    recRef.current = r;
    r.onspeechstart = () => stopSpeaking();
    r.onresult = (e: any) => {
      let text = '';
      let final = false;
      for (let i = e.resultIndex; i < e.results.length; i++) {
        text += e.results[i][0].transcript;
        if (e.results[i].isFinal) final = true;
      }
      setInterim(text);
      if (final && text.trim()) {
        setInterim('');
        finalRef.current(text.trim());
      }
    };
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    setListening(true);
    r.start();
  }, [supported, stopSpeaking, mode]);

  const stopListening = useCallback(() => {
    neuralRec.current?.stop();
    recRef.current?.stop();
    setListening(false);
  }, []);

  const speak = useCallback(
    (text: string) => {
      if (muted || !text) return;
      if (mode === 'neural') {
        neuralSpeech.current?.stop();
        const s = speakNeural(text, 'af_heart');
        neuralSpeech.current = s;
        setSpeaking(true);
        void s.done.catch(() => {}).finally(() => {
          if (neuralSpeech.current === s) neuralSpeech.current = undefined;
          setSpeaking(false);
        });
        return;
      }
      if (!window.speechSynthesis) return;
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      const v = pickVoice();
      if (v) u.voice = v;
      u.rate = 1.04;
      u.onstart = () => setSpeaking(true);
      u.onend = () => setSpeaking(false);
      u.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(u);
    },
    [muted, mode],
  );

  return { supported, listening, interim, speaking, muted, setMuted, listen, stopListening, speak, stopSpeaking, bargeInMs, status };
}
